import { Box, LoaderNew, SearchBar, SpacerNew, TextNew } from '@jfs/ui-new'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/core'
import { StackNavigationProp } from '@react-navigation/stack'
import React, { useEffect, useMemo, useState } from 'react'
import { Image as ImageComponent } from 'react-native'
import { Container } from '../../comps/Container'
import { useLazyApiData, useStores } from '../../data/store'
import { AuthenticatorItem, BbpsStackParamList, BillerItem, CategoryDetailsParams } from '../../nav/Bbps'
import { Screens } from '../../nav/Screens' // Ensure CategoryDetailsParams is imported
import { APIEndPoints } from '../../network/APIEndPoints'
import { getErrorMessage } from '../../util/utill'

const BILLER_IMAGE_BASE_URL = APIEndPoints.BILLER_Logo_IMAGE_BASE_URL

type ApiHandlerResult = {
  data: any
  state: 'pending' | 'fulfilled' | 'rejected' | undefined
}
type RechargeBillPaymentsNavigationProp = StackNavigationProp<
  BbpsStackParamList,
  typeof Screens.RECHARGE_BILL_PAYMENTS // The name of the current screen
>
type RechargeBillPaymentsRouteProp = RouteProp<BbpsStackParamList, typeof Screens.RECHARGE_BILL_PAYMENTS>


const BillerCard: React.FC<{ displayName: string; logoName: string; onPress: () => void }> = ({
  displayName,
  logoName,
  onPress,
}) => {
  const imageUrl = logoName ? `${BILLER_IMAGE_BASE_URL}${logoName}` : null
  return (
    <Box gap={10} marginTop={10}>
      <Box padding={14} flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'} onPress={onPress}>
        <Box flexDirection={'row'} alignItems={'center'} flex={1}>
          {imageUrl && (
            <Box marginRight={12}>
              <ImageComponent source={{ uri: imageUrl }} style={{ width: 32, height: 32 }} resizeMode="contain" />
            </Box>
          )}
          <TextNew fontSize={16} color="BLACK" fontWeight={'700'} numberOfLines={1}>
            {displayName}
          </TextNew>
        </Box>
      </Box>
    </Box>
  )
}
export const RechargeBillPayments: React.FC = () => {
  const route = useRoute<RechargeBillPaymentsRouteProp>()
  const [selectedBiller, setSelectedBiller] = useState<BillerItem | null>(null)
  const navigation = useNavigation<RechargeBillPaymentsNavigationProp>()
  const {
    mobileNumber = '8369186718',
    categoryId = '100006',
    categoryName,
    displayName,
  } = route.params as CategoryDetailsParams
  const [searchText, setSearchText] = useState('')
  const { callGetProductListBC, callGetAuthenticatorListBC } = useStores((root) => ({
    callGetProductListBC: root.BillPaymentAgentOnboard.callGetProductListBC,
    callGetAuthenticatorListBC: root.BillPaymentAgentOnboard.callGetAuthenticatorListBC,
  }))
  const [triggerGetProductListBC, productListHandler] = useLazyApiData(callGetProductListBC) as [
    (categoryId: number | string) => void,
    ApiHandlerResult,
  ]
  const { data: productListResponse, state: productListState } = productListHandler
  const billersError = getErrorMessage(productListHandler)
  const [triggerGetAuthenticatorList, authenticatorListHandler] = useLazyApiData(callGetAuthenticatorListBC) as [
    (productId: number | string) => Promise<AuthenticatorItem[]>,
    ApiHandlerResult,
  ]
  useEffect(() => {
    const numericCategoryId = typeof categoryId === 'string' ? Number(categoryId) : categoryId

    if (!isNaN(numericCategoryId) && numericCategoryId !== undefined) {
      triggerGetProductListBC(numericCategoryId)
    } else {
      console.error('Error: categoryId is invalid or missing:', categoryId)
    }
  }, [categoryId])
  const dynamicBillerData: BillerItem[] = Array.isArray(productListResponse) ? productListResponse : []
  const filteredBillerData = useMemo(() => {
    if (!searchText) {
      return dynamicBillerData
    }
    const lowerCaseSearchText = searchText.toLowerCase()
    return dynamicBillerData.filter((billerItem: BillerItem) => {
      const itemDisplayName = billerItem?.product?.displayName
      return itemDisplayName && itemDisplayName.toLowerCase().includes(lowerCaseSearchText)
    })
  }, [dynamicBillerData, searchText])
  const { data: authenticatorsResponse, state: authenticatorListState } = authenticatorListHandler
  useEffect(() => {
    if (selectedBiller && authenticatorListState === 'fulfilled' && authenticatorsResponse) {
      const productId = selectedBiller.product.id
      const customerCategoryDetails: CategoryDetailsParams = {
        mobileNumber,
        categoryId,
        categoryName,
        displayName,
      }
      console.log('DEBUG_NAV: Authenticators Response to be SENT:', authenticatorsResponse)
      navigation.navigate(Screens.BbpsBillerScreen, {
        billerDetails: selectedBiller,
        authenticators: authenticatorsResponse,
        productId: productId,
        categoryDetails: customerCategoryDetails,
      })
      setSelectedBiller(null)
    } else if (authenticatorListState === 'rejected') {
      console.error('API call failed during attempted navigation.')
      setSelectedBiller(null)
    }
  }, [
    authenticatorListState,
    selectedBiller,
    authenticatorsResponse,
    navigation,
    mobileNumber,
    categoryId,
    categoryName,
    displayName,
  ]) // Add all variables used inside the effect's dependency array

  const handleBillerCardPress = async (billerItem: BillerItem) => {
    const productId = billerItem?.product?.id
    const billerName = billerItem?.product?.displayName
    const billertype = billerItem?.product?.type
    const billerPartialPayType = billerItem?.product?.partialPaymentType
    const billerIsBBPS = billerItem?.product?.isBbps

    if (!productId) {
      console.error('Error: Biller item is missing a productId.', billerItem)
      console.log('Error: Biller item is missing a productId.', billerItem)
      return
    }

    setSelectedBiller(billerItem)
    triggerGetAuthenticatorList(productId)
  }

  // --- Conditional Rendering ---
  const isFetchingAnyApi = productListState === 'pending' || authenticatorListState === 'pending'
  const anyApiError = billersError || getErrorMessage(authenticatorListHandler)

  if (isFetchingAnyApi) {
    return (
      <Box flex={1} justifyContent="center" alignItems="center">
        {/* <PlanSkeletonList /> */}
        <LoaderNew type="large" color="BRAND_PRIMARY_ACTION" />
        <SpacerNew size={15} />
        <TextNew>Loading Billers...</TextNew>
      </Box>
    )
  }

  if (anyApiError) {
    return (
      <Container showFooter={false} overflow="auto">
      <Box>
        <Box flexDirection={'column'}>
          <Box backgroundColor="WHITE" flexDirection={'column'}>
            <SearchBar
              onChange={setSearchText} // Simplified setter
              placeholder="Search Operators"
              value={searchText}
            />
          </Box>
        </Box>
      </Box>

      <Box>
        <TextNew>No Billers available for this category.</TextNew>
      </Box>

      </Container>
    )
    return <TextNew color="RED">Error fetching data: {anyApiError}</TextNew>
  }

  if (filteredBillerData.length === 0 && !searchText) {
    return <TextNew>No Billers available for this category.</TextNew>
  }

  return (
    <Container showFooter={false} overflow="auto">
      <Box>
        <Box flexDirection={'column'}>
          <Box backgroundColor="WHITE" flexDirection={'column'}>
            <SearchBar
              onChange={setSearchText} // Simplified setter
              placeholder="Search Operators"
              value={searchText}
            />
          </Box>
        </Box>
      </Box>
      {console.log('filteredBillerData filteredBillerData', filteredBillerData)}
      {filteredBillerData.length > 0 ? (
        filteredBillerData.map((billerItem: BillerItem, index: number) => (
          <BillerCard
            key={billerItem.product.id || index}
            displayName={billerItem.product.displayName}
            logoName={billerItem.product.logoName}
            onPress={() => handleBillerCardPress(billerItem)}
          />
        ))
      ) : (
        <Box marginTop={20}>
          <TextNew textAlign="center" color="LIGHT_GREY">
            No operators found for "{searchText}".
          </TextNew>
        </Box>
      )}
    </Container>
  )
}
